import { useMemo } from "react";
import { filter, maxBy, orderBy, reverse, sumBy, times } from "lodash";
import { natural } from "@tootools/sort";
import { Gauge, Order, Sort } from "./model";

const getUpperMultiple = (length: number, limit: number) =>
  limit * Math.ceil(length / limit);

const getGaugesSortedByAlpha = (gauges: Gauge[], order: Order) => {
  const sortedGauges = natural(gauges, "label");
  return order === "asc" ? reverse(sortedGauges) : sortedGauges;
};

const getGaugesSortedByValue = (gauges: Gauge[], order: Order) =>
  orderBy(gauges, "count", order);
export const getSortedGauges = (gauges: Gauge[], sort: Sort, order: Order) => {
  if (sort === "alpha") {
    return getGaugesSortedByAlpha(gauges, order);
  }
  if (sort === "mainValue") {
    return getGaugesSortedByValue(gauges, order);
  }
  return gauges;
};

const getGaugesWithMissingRows = (gauges: Gauge[], limit: number) => {
  const upperMultiple = getUpperMultiple(gauges.length, limit);
  const missingRowsCount = upperMultiple - gauges.length;
  if (!missingRowsCount) {
    return gauges;
  }
  return [
    ...gauges,
    ...times(missingRowsCount, (num: number) => {
      return {
        id: "phantom_" + num,
        count: null,
        label: null,
        isPhantom: true,
      };
    }),
  ];
};

export const getDisplayedGauges = (
  gauges: Gauge[],
  displayNullValues: boolean
) => (displayNullValues ? gauges : filter(gauges, "count"));

export const useSortedGauges = (
  gauges: Gauge[],
  limit: number,
  sort: Sort,
  order: Order,
  displayNullValues: boolean,
  isFullscreen: boolean
) =>
  useMemo(() => {
    const displayedGauges = getDisplayedGauges(gauges, displayNullValues);
    const sortedGauges = getSortedGauges(displayedGauges, sort, order);
    const gaugesWithMissingRows = getGaugesWithMissingRows(sortedGauges, limit);
    return {
      sortedGauges: isFullscreen ? sortedGauges : gaugesWithMissingRows,
      paginationTotal: sortedGauges.length,
    };
  }, [gauges, displayNullValues, sort, order, limit, isFullscreen]);

export const getMaxCount = (gauges: Gauge[]) => maxBy(gauges, "count").count;

export const getFullTotal = (total: number, series: Gauge[]) =>
  total ?? sumBy(series, "count");
